/**
 * 全局实体 flyout 宿主：`useEntityFlyout()?.open(scheme, id)` 在当前页面打开对应实体的详情 flyout。
 * 写法照 checkRegister 的 SourceFlyoutHost：轻量 context 负责「打开」，宿主按类型渲染已有的 flyout 组件（懒加载）。
 * Provider 外 useEntityFlyout 返回 null，调用方应退化为纯文本 / 普通链接。
 * 各 flyout 自己按查看者当前权限取数，403 / 不存在由 flyout 如实呈现。
 */
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { reservationsApi } from '@/api'
import { adaptReservationForFlyout } from '@/utils/reservationAdapter'
import { extractApiErrorMessage } from '@/utils/apiError'
import type { FlyoutReservation } from '@/types/reservation'
import { asSnowflakeId } from '@/types/snowflake'
import { isEntityScheme, type EntityScheme } from './entityLinks'

const TaskDetail = lazy(() => import('@/components/tasks/TaskDetail').then((m) => ({ default: m.TaskDetail })))
const ReservationFlyout = lazy(() => import('@/components/reservations/ReservationFlyout').then((m) => ({ default: m.ReservationFlyout })))
const IntentDetail = lazy(() => import('@/components/intents/IntentDetail').then((m) => ({ default: m.IntentDetail })))

interface EntityFlyoutApi {
  canOpen: (scheme: string) => boolean
  open: (scheme: string, id: string) => void
}

const EntityFlyoutContext = createContext<EntityFlyoutApi | null>(null)

export const useEntityFlyout = () => useContext(EntityFlyoutContext)

/** 预订 flyout 要整条预订数据（不是 id）：按 id 取一次再打开，取不到就提示并关掉——写法同任务详情里点 RES 标签。 */
function ReservationHost({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation('common')
  const [reservation, setReservation] = useState<FlyoutReservation | null>(null)
  useEffect(() => {
    let live = true
    setReservation(null)
    reservationsApi.getReservation(id)
      .then((full) => { if (live) setReservation(adaptReservationForFlyout(full as never)) })
      .catch((err) => {
        if (!live) return
        toast.error(extractApiErrorMessage(err, t('entityFlyout.reservationLoadFailed')))
        onClose()
      })
    return () => { live = false }
  }, [id, onClose, t])
  return <ReservationFlyout reservation={reservation} open={!!reservation} onOpenChange={(o) => { if (!o) onClose() }} />
}

export function EntityFlyoutProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ scheme: EntityScheme; id: string } | null>(null)
  const close = useCallback(() => setCurrent(null), [])
  const api = useMemo<EntityFlyoutApi>(
    () => ({
      canOpen: isEntityScheme,
      open: (scheme, id) => {
        if (isEntityScheme(scheme)) setCurrent({ scheme, id })
      },
    }),
    [],
  )

  return (
    <EntityFlyoutContext.Provider value={api}>
      {children}
      <Suspense fallback={null}>
        {current?.scheme === 'task' && <TaskDetail taskId={asSnowflakeId(current.id)} isOpen onClose={close} />}
        {current?.scheme === 'reservation' && <ReservationHost id={current.id} onClose={close} />}
        {current?.scheme === 'intent' && <IntentDetail intentId={current.id} isOpen onClose={close} />}
      </Suspense>
    </EntityFlyoutContext.Provider>
  )
}
